'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const solutionRoutes = [
  '/expand',
  '/replace-ai-sdr',
  '/just-raised-a-b',
  '/first-sdr-function',
  '/switching-providers',
];

const solutions = [
  { href: '/expand', label: 'Extend your sales team', tag: 'Expand' },
  { href: '/replace-ai-sdr', label: 'Your AI SDR did not work out', tag: 'Replace' },
  { href: '/just-raised-a-b', label: 'Just raised Series A or B', tag: 'Scale' },
  { href: '/first-sdr-function', label: 'Your first SDR function', tag: 'Start' },
  { href: '/switching-providers', label: 'Your provider got acquired', tag: 'Switch' },
];

export default function Header() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDdOpen(false);
  }, [pathname]);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(href);
  };

  const isSolutionActive = solutionRoutes.some((r) => pathname === r);

  return (
    <>
      <nav
        className="sticky top-0 z-50 bg-navy-deep border-b border-line-navy"
      >
        <div className="max-w-site mx-auto px-7 h-[70px] flex items-center gap-8">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
            <AppLogo size={30} />
            <span className="font-display text-xl font-bold tracking-widest text-white">
              Command<b className="text-primary">VA</b>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6 ml-auto">
            <Link
              href="/homepage"
              data-nav="method"
              className={`text-sm font-semibold transition-all px-3 py-1.5 rounded-lg ${
                isActive('/homepage') ? 'text-primary bg-white/15' : 'text-white hover:text-primary hover:bg-white/10'
              }`}
            >
              How we work
            </Link>

            {/* Solutions dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setDdOpen(true)}
              onMouseLeave={() => setDdOpen(false)}
            >
              <button
                className={`text-sm font-semibold transition-all flex items-center gap-1 px-3 py-1.5 rounded-lg ${
                  isSolutionActive ? 'text-primary bg-white/15' : 'text-white hover:text-primary hover:bg-white/10'
                }`}
                onClick={() => setDdOpen(!ddOpen)}
                aria-expanded={ddOpen}
              >
                Solutions
                <span className="text-[11px] opacity-70">▾</span>
              </button>

              {/* Dropdown — always rendered but hidden via opacity/pointer-events to keep hover zone intact */}
              <div
                className={`absolute left-[-14px] pt-2 z-50 transition-all duration-150 ${
                  ddOpen ? 'opacity-100 pointer-events-auto translate-y-0' : 'opacity-0 pointer-events-none -translate-y-1'
                }`}
                style={{ top: '100%' }}
              >
                <div className="bg-navy-deep border border-line-navy rounded-xl p-2.5 min-w-[290px] shadow-[0_18px_44px_rgba(0,0,0,0.6)]">
                  {solutions.map((s) => (
                    <Link
                      key={s.href}
                      href={s.href}
                      className="block px-3.5 py-2.5 rounded-lg hover:bg-navy-panel transition-colors group"
                    >
                      <span className="block font-mono text-[10.5px] tracking-[0.14em] text-primary uppercase mb-0.5">
                        {s.tag}
                      </span>
                      <span className="text-sm text-white group-hover:text-primary transition-colors">
                        {s.label}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              href="/industries"
              data-nav="industries"
              className={`text-sm font-semibold transition-all px-3 py-1.5 rounded-lg ${
                isActive('/industries') ? 'text-primary bg-white/15' : 'text-white hover:text-primary hover:bg-white/10'
              }`}
            >
              Industries
            </Link>

            <Link
              href="/pricing"
              data-nav="pricing"
              className={`text-sm font-semibold transition-all px-3 py-1.5 rounded-lg ${
                isActive('/pricing') ? 'text-primary bg-white/15' : 'text-white hover:text-primary hover:bg-white/10'
              }`}
            >
              Pricing
            </Link>

            <Link
              href="/math"
              data-nav="math"
              className={`text-sm font-semibold transition-all px-3 py-1.5 rounded-lg ${
                isActive('/math') ? 'text-primary bg-white/15' : 'text-white hover:text-primary hover:bg-white/10'
              }`}
            >
              The math
            </Link>

            <Link
              href="/blog"
              data-nav="blog"
              className={`text-sm font-semibold transition-all px-3 py-1.5 rounded-lg ${
                isActive('/blog') ? 'text-primary bg-white/15' : 'text-white hover:text-primary hover:bg-white/10'
              }`}
            >
              Blog
            </Link>

            <Link
              href="/request-meeting"
              className="ml-1.5 inline-flex items-center gap-2.5 font-semibold text-sm px-5 py-3 rounded-[10px] bg-primary text-navy-deep border border-transparent transition-all duration-200 hover:bg-gold-bright hover:-translate-y-px hover:shadow-gold"
            >
              Book a strategy call
            </Link>
          </div>

          {/* Burger */}
          <button
            className="lg:hidden ml-auto text-white text-2xl p-2 -mr-2"
            aria-label="Toggle menu"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden bg-navy-deep border-t border-line-navy px-7 pb-5 pt-2">
            <Link
              href="/homepage"
              className="block py-3 text-white text-base font-semibold border-b border-white/[0.08] hover:text-primary transition-colors"
            >
              How we work
            </Link>
            <Link
              href="/industries"
              className="block py-3 text-white text-base font-semibold border-b border-white/[0.08] hover:text-primary transition-colors"
            >
              Industries
            </Link>
            <Link
              href="/pricing"
              className="block py-3 text-white text-base font-semibold border-b border-white/[0.08] hover:text-primary transition-colors"
            >
              Pricing
            </Link>
            <Link
              href="/math"
              className="block py-3 text-white text-base font-semibold border-b border-white/[0.08] hover:text-primary transition-colors"
            >
              The math
            </Link>
            <Link
              href="/blog"
              className="block py-3 text-white text-base font-semibold border-b border-white/[0.08] hover:text-primary transition-colors"
            >
              Blog
            </Link>
            <div className="border-b border-white/[0.08]">
              <p className="py-3 text-white text-base font-semibold">Solutions</p>
              {solutions.map((s) => (
                <Link
                  key={s.href}
                  href={s.href}
                  className="block pl-4 py-2.5 text-white/80 text-sm hover:text-primary transition-colors"
                >
                  {s.label}
                </Link>
              ))}
            </div>
            <Link
              href="/request-meeting"
              className="block py-3 text-primary font-semibold text-base"
            >
              Book a strategy call →
            </Link>
          </div>
        )}
      </nav>
    </>
  );
}